import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  MenuItem,
  Grid2 as Grid,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Alert,
  Tooltip,
  Divider,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  VpnKey as KeyIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  ContentCopy as CopyIcon,
  Visibility as ViewIcon,
  VisibilityOff as HideIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';

interface OAuthClient {
  id: string;
  clientId: string;
  clientSecret: string;
  name: string;
  description: string;
  redirectUris: string[];
  scopes: string[];
  grantTypes: string[];
  status: 'active' | 'inactive' | 'revoked';
  lastUsed?: string;
  createdAt: string;
}

// Mock data
const mockClients: OAuthClient[] = [
  {
    id: '1',
    clientId: 'pagali-web-app',
    clientSecret: 'pk_prod_a1b2c3d4e5f6g7h8i9j0',
    name: 'Pagali Web Application',
    description: 'Cliente OAuth para aplicação web principal',
    redirectUris: ['https://pagali.cv/callback', 'https://app.pagali.cv/auth/callback'],
    scopes: ['read:payments', 'write:payments', 'read:invoices', 'write:invoices'],
    grantTypes: ['authorization_code', 'refresh_token'],
    status: 'active',
    lastUsed: '2026-01-09 10:30:00',
    createdAt: '2024-01-01',
  },
  {
    id: '2',
    clientId: 'mobile-app-ios',
    clientSecret: 'pk_prod_k1l2m3n4o5p6q7r8s9t0',
    name: 'Pagali Mobile iOS',
    description: 'Cliente OAuth para aplicação móvel iOS',
    redirectUris: ['pagali://oauth/callback'],
    scopes: ['read:payments', 'read:invoices', 'read:profile'],
    grantTypes: ['authorization_code', 'refresh_token'],
    status: 'active',
    lastUsed: '2026-01-09 09:15:00',
    createdAt: '2024-03-15',
  },
  {
    id: '3',
    clientId: 'mobile-app-android',
    clientSecret: 'pk_prod_u1v2w3x4y5z6a7b8c9d0',
    name: 'Pagali Mobile Android',
    description: 'Cliente OAuth para aplicação móvel Android',
    redirectUris: ['pagali://oauth/callback'],
    scopes: ['read:payments', 'read:invoices', 'read:profile'],
    grantTypes: ['authorization_code', 'refresh_token'],
    status: 'active',
    lastUsed: '2026-01-09 11:00:00',
    createdAt: '2024-03-15',
  },
  {
    id: '4',
    clientId: 'api-integration-cvt',
    clientSecret: 'pk_prod_e1f2g3h4i5j6k7l8m9n0',
    name: 'CV Telecom API Integration',
    description: 'Cliente para integração API da CV Telecom',
    redirectUris: [],
    scopes: ['read:payments', 'write:payments', 'webhook:receive'],
    grantTypes: ['client_credentials'],
    status: 'active',
    lastUsed: '2026-01-09 11:45:00',
    createdAt: '2024-01-15',
  },
  {
    id: '5',
    clientId: 'dashboard-analytics',
    clientSecret: 'pk_prod_o1p2q3r4s5t6u7v8w9x0',
    name: 'Analytics Dashboard',
    description: 'Cliente para dashboard de analytics e relatórios',
    redirectUris: ['https://analytics.pagali.cv/callback'],
    scopes: ['read:payments', 'read:invoices', 'read:analytics'],
    grantTypes: ['authorization_code', 'refresh_token'],
    status: 'active',
    lastUsed: '2026-01-08 16:30:00',
    createdAt: '2024-06-01',
  },
  {
    id: '6',
    clientId: 'test-client-old',
    clientSecret: 'pk_test_y1z2a3b4c5d6e7f8g9h0',
    name: 'Test Client (Deprecated)',
    description: 'Cliente de teste - já não é usado',
    redirectUris: ['http://localhost:3000/callback'],
    scopes: ['read:payments'],
    grantTypes: ['authorization_code'],
    status: 'revoked',
    lastUsed: '2025-11-15 14:20:00',
    createdAt: '2024-01-01',
  },
];

const availableScopes = [
  'read:payments',
  'write:payments',
  'read:invoices',
  'write:invoices',
  'read:profile',
  'write:profile',
  'read:analytics',
  'webhook:receive',
  'admin:full',
];

const availableGrantTypes = [
  'authorization_code',
  'client_credentials',
  'refresh_token',
  'password',
];

const OAuthClients: React.FC = () => {
  const [clients, setClients] = useState<OAuthClient[]>(mockClients);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [editDialog, setEditDialog] = useState(false);
  const [selectedClient, setSelectedClient] = useState<OAuthClient | null>(null);
  const [showSecret, setShowSecret] = useState<Record<string, boolean>>({});
  const [formData, setFormData] = useState<{
    clientId: string;
    name: string;
    description: string;
    redirectUris: string;
    scopes: string[];
    grantTypes: string[];
    status: 'active' | 'inactive' | 'revoked';
  }>({
    clientId: '',
    name: '',
    description: '',
    redirectUris: '',
    scopes: [],
    grantTypes: [],
    status: 'active',
  });

  const getStatusChip = (status: string) => {
    if (status === 'active') {
      return <Chip size="small" color="success" label="Activo" icon={<ActiveIcon />} />;
    } else if (status === 'revoked') {
      return <Chip size="small" color="error" label="Revogado" icon={<InactiveIcon />} />;
    } else {
      return <Chip size="small" color="default" label="Inactivo" icon={<InactiveIcon />} />;
    }
  };

  const filteredClients = clients.filter(client => {
    const matchesSearch = searchTerm === '' ||
      client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.clientId.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || client.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const stats = {
    total: clients.length,
    active: clients.filter(c => c.status === 'active').length,
    inactive: clients.filter(c => c.status === 'inactive').length,
    revoked: clients.filter(c => c.status === 'revoked').length,
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenEdit = (client?: OAuthClient) => {
    if (client) {
      setSelectedClient(client);
      setFormData({
        clientId: client.clientId,
        name: client.name,
        description: client.description,
        redirectUris: client.redirectUris.join('\n'),
        scopes: client.scopes,
        grantTypes: client.grantTypes,
        status: client.status,
      });
    } else {
      setSelectedClient(null);
      setFormData({
        clientId: '',
        name: '',
        description: '',
        redirectUris: '',
        scopes: [],
        grantTypes: ['authorization_code'],
        status: 'active',
      });
    }
    setEditDialog(true);
  };

  const handleCloseEdit = () => {
    setEditDialog(false);
    setSelectedClient(null);
  };

  const generateClientSecret = () => {
    const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
    let secret = 'pk_prod_';
    for (let i = 0; i < 20; i++) {
      secret += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return secret;
  };

  const handleSave = () => {
    if (selectedClient) {
      setClients(clients.map(c =>
        c.id === selectedClient.id
          ? {
              ...c,
              ...formData,
              redirectUris: formData.redirectUris.split('\n').filter(u => u.trim()),
            }
          : c
      ));
    } else {
      const newClient: OAuthClient = {
        id: crypto.randomUUID(),
        ...formData,
        clientSecret: generateClientSecret(),
        redirectUris: formData.redirectUris.split('\n').filter(u => u.trim()),
        createdAt: new Date().toISOString().split('T')[0],
      };
      setClients([...clients, newClient]);
    }
    handleCloseEdit();
  };

  const handleCopySecret = (secret: string) => {
    navigator.clipboard.writeText(secret);
  };

  const handleToggleSecret = (clientId: string) => {
    setShowSecret(prev => ({ ...prev, [clientId]: !prev[clientId] }));
  };

  const handleRegenerateSecret = (client: OAuthClient) => {
    if (window.confirm(`Tem certeza que deseja regenerar o Client Secret para "${client.name}"? Isto irá invalidar o secret actual.`)) {
      const newSecret = generateClientSecret();
      setClients(clients.map(c =>
        c.id === client.id
          ? { ...c, clientSecret: newSecret }
          : c
      ));
    }
  };

  const handleToggleScope = (scope: string) => {
    setFormData(prev => ({
      ...prev,
      scopes: prev.scopes.includes(scope)
        ? prev.scopes.filter(s => s !== scope)
        : [...prev.scopes, scope],
    }));
  };

  const handleToggleGrantType = (grantType: string) => {
    setFormData(prev => ({
      ...prev,
      grantTypes: prev.grantTypes.includes(grantType)
        ? prev.grantTypes.filter(g => g !== grantType)
        : [...prev.grantTypes, grantType],
    }));
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight={600}>
          Clientes OAuth
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenEdit()}>
          Novo Cliente
        </Button>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        Clientes OAuth são usados para autenticação e autorização de aplicações externas que acedem à API Pagali.
      </Alert>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <KeyIcon color="primary" />
                <Box>
                  <Typography variant="h5" fontWeight={700}>{stats.total}</Typography>
                  <Typography variant="caption" color="text.secondary">Total Clientes</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ActiveIcon color="success" />
                <Box>
                  <Typography variant="h5" fontWeight={700}>{stats.active}</Typography>
                  <Typography variant="caption" color="text.secondary">Activos</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <InactiveIcon color="warning" />
                <Box>
                  <Typography variant="h5" fontWeight={700}>{stats.inactive}</Typography>
                  <Typography variant="caption" color="text.secondary">Inactivos</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <InactiveIcon color="error" />
                <Box>
                  <Typography variant="h5" fontWeight={700}>{stats.revoked}</Typography>
                  <Typography variant="caption" color="text.secondary">Revogados</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, md: 8 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Pesquisar clientes OAuth..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                size="small"
                select
                label="Estado"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">Todos</MenuItem>
                <MenuItem value="active">Activos</MenuItem>
                <MenuItem value="inactive">Inactivos</MenuItem>
                <MenuItem value="revoked">Revogados</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Clients Table */}
      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width={180}>Client ID</TableCell>
                <TableCell>Nome</TableCell>
                <TableCell width={200}>Client Secret</TableCell>
                <TableCell width={80} align="center">Scopes</TableCell>
                <TableCell width={140}>Última Utilização</TableCell>
                <TableCell width={100}>Estado</TableCell>
                <TableCell width={150} align="center">Acções</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredClients
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((client) => (
                <TableRow key={client.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600} fontFamily="monospace" fontSize="0.75rem">
                      {client.clientId}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {client.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {client.description}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="body2" fontFamily="monospace" fontSize="0.7rem">
                        {showSecret[client.id] ? client.clientSecret : '••••••••••••••••••••'}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => handleToggleSecret(client.id)}
                      >
                        {showSecret[client.id] ? <HideIcon fontSize="small" /> : <ViewIcon fontSize="small" />}
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleCopySecret(client.clientSecret)}
                      >
                        <CopyIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Chip size="small" label={client.scopes.length} variant="outlined" />
                  </TableCell>
                  <TableCell>
                    {client.lastUsed ? (
                      <Typography variant="body2" fontSize="0.7rem" color="text.secondary">
                        {client.lastUsed}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.disabled">Nunca</Typography>
                    )}
                  </TableCell>
                  <TableCell>{getStatusChip(client.status)}</TableCell>
                  <TableCell align="center">
                    <Tooltip title="Regenerar Secret">
                      <IconButton
                        size="small"
                        onClick={() => handleRegenerateSecret(client)}
                        disabled={client.status === 'revoked'}
                      >
                        <RefreshIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <IconButton size="small" onClick={() => handleOpenEdit(client)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      disabled={client.status === 'revoked'}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filteredClients.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50]}
          labelRowsPerPage="Linhas por página:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
        />
      </Paper>

      {/* Edit/Create Dialog */}
      <Dialog open={editDialog} onClose={handleCloseEdit} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <KeyIcon />
          {selectedClient ? 'Editar Cliente OAuth' : 'Novo Cliente OAuth'}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Client ID"
                value={formData.clientId}
                onChange={(e) => setFormData({ ...formData, clientId: e.target.value })}
                disabled={!!selectedClient}
                helperText="Identificador único do cliente"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                select
                label="Estado"
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
              >
                <MenuItem value="active">Activo</MenuItem>
                <MenuItem value="inactive">Inactivo</MenuItem>
                <MenuItem value="revoked">Revogado</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Nome"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Descrição"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                multiline
                rows={2}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Redirect URIs"
                value={formData.redirectUris}
                onChange={(e) => setFormData({ ...formData, redirectUris: e.target.value })}
                multiline
                rows={3}
                helperText="Um URI por linha. Deixe vazio para client_credentials flow."
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" gutterBottom>Scopes Permitidos</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {availableScopes.map(scope => (
                  <Chip
                    key={scope}
                    label={scope}
                    onClick={() => handleToggleScope(scope)}
                    color={formData.scopes.includes(scope) ? 'primary' : 'default'}
                    variant={formData.scopes.includes(scope) ? 'filled' : 'outlined'}
                    size="small"
                  />
                ))}
              </Box>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" gutterBottom>Grant Types</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {availableGrantTypes.map(grantType => (
                  <Chip
                    key={grantType}
                    label={grantType}
                    onClick={() => handleToggleGrantType(grantType)}
                    color={formData.grantTypes.includes(grantType) ? 'secondary' : 'default'}
                    variant={formData.grantTypes.includes(grantType) ? 'filled' : 'outlined'}
                    size="small"
                  />
                ))}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEdit}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!formData.clientId || !formData.name || formData.scopes.length === 0 || formData.grantTypes.length === 0}
          >
            {selectedClient ? 'Guardar' : 'Criar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OAuthClients;
